import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import api from '@/lib/api';
import { toast } from '@/lib/toast';
import { useAuth } from '@/lib/auth';

// Minimal markdown-ish rendering for the EULA text: #/## headers, bold
// intro line, plain paragraphs. Keeps the backend file human-editable
// without pulling in a markdown library.
const renderEula = (text) =>
  text.split(/\n{2,}/).map((block, i) => {
    const t = block.trim();
    if (t.startsWith('## ')) {
      return (
        <Typography key={i} variant="subtitle2" fontWeight={700} sx={{ mt: 2 }}>
          {t.replace(/^## /, '')}
        </Typography>
      );
    }
    if (t.startsWith('# ')) {
      return (
        <Typography key={i} variant="h6" fontWeight={700}>
          {t.replace(/^# /, '')}
        </Typography>
      );
    }
    return (
      <Typography key={i} variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\n/g, ' ')}
      </Typography>
    );
  });

// Blocking agreement gate: after login, users who haven't accepted the
// current EULA version must agree before using the app. Acceptance is
// recorded server-side with a timestamp and version.
const EulaGate = () => {
  const { user, isAuthenticated, refreshUser, logout } = useAuth();
  const [eula, setEula] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    api
      .get('/auth/eula')
      .then((res) => setEula(res.data))
      .catch(() => setEula(null));
    // The cached localStorage user may predate the EULA fields.
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const needsAcceptance = Boolean(
    isAuthenticated && eula && user && user.eula_version !== eula.version,
  );

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await api.post('/auth/accept-eula');
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record acceptance');
    } finally {
      setAccepting(false);
    }
  };

  if (!needsAcceptance) return null;

  return (
    <Dialog open maxWidth="sm" fullWidth disableEscapeKeyDown data-testid="eula-dialog">
      <DialogTitle sx={{ fontWeight: 700 }}>License agreement</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '55vh' }}>
        <Box>{renderEula(eula.text)}</Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={logout} disabled={accepting} color="inherit">
          Decline & sign out
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={accepting}
          data-testid="eula-accept-button"
        >
          {accepting ? 'Saving…' : 'I agree'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EulaGate;
